// src/pages/VocationalUniversityResults.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/VocationalUniversityResults.css";
import logo from "../assets/logo1.png";
import logoMirai from "../assets/mirai_logo.png";
import 'bootstrap-icons/font/bootstrap-icons.css';
import Navbar from '../components/Navbar';
import { PLATFORM_PDF_FOOTER } from '../constants/platform';

// React-PDF
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
  Image,
} from "@react-pdf/renderer";

// === Estilos PDF ===
const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 12, fontFamily: "Helvetica" },
  header: { textAlign: "center", marginBottom: 20 },
  title: { fontSize: 18, fontWeight: "bold", color: "#004d40", marginTop: 10 },
  logo: { width: 60, height: 60, margin: "0 auto" },
  studentInfo: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 8,
    color: "#333",
  },
  card: {
    border: "1pt solid #ccc",
    borderRadius: 6,
    padding: 8,
    marginBottom: 10,
  },
  recommendation: { fontSize: 11, marginTop: 4, textAlign: "justify" },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    fontSize: 9,
    textAlign: "center",
    color: "#666",
  },
});

// === Documento PDF ===
const ResultsPDF = ({ user, results, recommendations }) => {
  const date = new Date().toLocaleDateString();
  const rec = recommendations[results.bestArea];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Marca de agua */}
        <Image
          src={logoMirai}
          style={{
            position: "absolute",
            top: "30%",
            left: "20%",
            width: 300,
            height: 300,
            opacity: 0.1,
          }}
        />

        {/* Header */}
        <View style={styles.header}>
          <Image src={logoMirai} style={styles.logo} />
          <Text style={styles.title}>Resultados de Orientación Vocacional</Text>
          <Text>Nivel Superior</Text>
        </View>

        {/* Datos */}
        <View style={styles.studentInfo}>
          <Text>Nombre del estudiante: {user?.name || "N/D"}</Text>
          <Text>Fecha: {date}</Text>
          <Text>
            Área con mayor afinidad: {results.bestArea} ({results.bestScore} pts)
          </Text>
        </View>

        {/* Resumen por área */}
        <Text style={styles.sectionTitle}>Resumen por Área:</Text>
        {Object.entries(results.totalScores || {}).map(([area, data]) => (
          <View key={area} style={styles.card}>
            <Text>
              {area} – {data.level} ({data.score} pts)
            </Text>
          </View>
        ))}

        {/* Carreras y universidades */}
        {rec && (
          <>
            <Text style={styles.sectionTitle}>
              Carreras Universitarias Sugeridas:
            </Text>
            <View style={styles.card}>
              {rec.careers.map((c, idx) => (
                <Text key={idx} style={styles.recommendation}>
                  • {c}
                </Text>
              ))}
            </View>

            <Text style={styles.sectionTitle}>
              Universidades que las Ofrecen:
            </Text>
            <View style={styles.card}>
              {rec.schools.map((s, idx) => (
                <Text key={idx} style={styles.recommendation}>
                  • {s}
                </Text>
              ))}
            </View>
          </>
        )}

        {/* Footer */}
        <Text style={styles.footer}>
          {PLATFORM_PDF_FOOTER}
        </Text>
      </Page>
    </Document>
  );
};

const VocationalUniversityResults = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const navigate = useNavigate();

  const vocationalRecommendations = {
    "Ciencia y Tecnología": {
      careers: [
        "Ingeniería en Sistemas Computacionales", "Ingeniería en Software", "Ingeniería en Tecnologías de la Información", "Ciencias de la Computación", "Ingeniería Mecatrónica", "Ingeniería Mecánica", "Ingeniería Industrial", "Ingeniería en Automatización", "Ingeniería Electrónica", "Ingeniería en Telecomunicaciones", "Ingeniería Eléctrica", "Ingeniería en Electrónica y Comunicaciones", "Ingeniería en Inteligencia Artificial", "Ingeniería en Robótica", "Ingeniería en Machine Learning y Data Science", "Ciencia de Datos (Data Science)", "Ingeniería Biomédica", "Ingeniería en Dispositivos Médicos", "Bioingeniería", "Ingeniería en Energías Renovables", "Ingeniería en Energía", "Ingeniería Ambiental", "Ingeniería Química", "Ingeniería en Nanotecnología", "Ingeniería en Materiales", "Ingeniería Aeroespacial", "Ingeniería Aeronáutica", "Ingeniería Naval", "Ingeniería Civil", "Ingeniería en Construcción", "Ingeniería Geofísica", "Ingeniería Petrolera", "Ingeniería en Minas y Metalurgia", "Ingeniería en Ciberseguridad", "Seguridad Informática", "Redes y Telecomunicaciones", "Ingeniería en Internet de las Cosas (IoT)", "Ingeniería en Blockchain y Criptomonedas", "Matemáticas Aplicadas", "Actuaría", "Estadística", "Física", "Física Aplicada", "Astronomía", "Geofísica", "Química", "Bioquímica", "Biotecnología", "Bioinformática", "Genética", "Microbiología", "Nanociencias y Nanotecnología", "Ingeniería en Alimentos", "Tecnología de Alimentos", "Agronomía (con enfoque tecnológico)", "Ingeniería Agrícola", "Ingeniería Forestal", "Oceanografía", "Ciencias de la Tierra", "Geología", "Meteorología", "Ingeniería Topográfica y Geodésica"
      ],
      schools: [
        "IPN - Instituto Politécnico Nacional: ESIME, ESCOM, UPIICSA, UPIITA, UPIBI (CDMX y Edo. de México)", "Centro Universitario Grupo Sol (CUGS): Ingeniería en Sistemas, Tecnologías de la Información, programas técnicos en computación y automatización", "UNAM - Universidad Nacional Autónoma de México: Facultad de Ingeniería, Facultad de Ciencias (Ciudad Universitaria, CDMX)", "Tec de Monterrey - Instituto Tecnológico y de Estudios Superiores de Monterrey: Múltiples campus con todas las ingenierías", "UNITEC - Universidad Tecnológica de México: Ingenierías en Sistemas, Mecatrónica, Industrial, Civil", "Universidad Iberoamericana (IBERO): Ingenierías con enfoque interdisciplinario e innovación", "Universidad La Salle: Ingeniería, Arquitectura, Ingenierías ambientales", "Universidad Anáhuac: Ingenierías con enfoque internacional", "Universidad Panamericana (UP): Ingenierías y ciencias exactas", "Universidad de las Américas Puebla (UDLAP): Amplia oferta en ingenierías", "Universidad Autónoma Metropolitana (UAM): Campus Azcapotzalco, Iztapalapa (Ingenierías y Ciencias)", "Universidad Autónoma del Estado de México (UAEM): Facultades de Ingeniería", "Universidad de Guadalajara (UDG): CUCEI - Centro Universitario de Ciencias Exactas e Ingenierías", "Universidad Autónoma de Nuevo León (UANL): FIME - Facultad de Ingeniería Mecánica y Eléctrica, FCFM", "Benemérita Universidad Autónoma de Puebla (BUAP): Facultades de Ciencias Físico Matemáticas e Ingeniería", "Universidad Veracruzana (UV): Facultades de Ingeniería en varios campus", "Universidad Autónoma de San Luis Potosí (UASLP): Ingenierías y Ciencias", "Universidad de Guanajuato (UG): Campus Irapuato-Salamanca, Campus León", "Universidad Autónoma de Querétaro (UAQ): Facultad de Ingeniería", "Instituto Tecnológico de Estudios Superiores de Occidente (ITESO): Ingenierías en Guadalajara", "Tecnológico Nacional de México (TecNM): 254 institutos tecnológicos en todo el país", "Universidad Autónoma de Baja California (UABC): Facultades de Ingeniería", "Universidad de Sonora (UNISON): División de Ingeniería", "Universidad Autónoma de Coahuila (UAdeC): Facultades de Ingeniería", "Universidad Autónoma de Chihuahua (UACH): Facultad de Ingeniería", "Universidad Autónoma de Yucatán (UADY): Facultad de Ingeniería", "Universidad Autónoma de Aguascalientes (UAA): Centro de Ciencias Básicas", "Instituto Tecnológico Autónomo de México (ITAM): Ingenierías con enfoque en negocios", "CETYS Universidad: Campus en Baja California", "Universidad Rosario Castellanos: Ingenierías (según oferta)", "Universidad Insurgentes: Programas tecnológicos"
      ]
    },
    "Ciencias Sociales": {
      careers: [
        "Derecho", "Derecho Internacional", "Criminología y Criminalística", "Ciencias Forenses",
        "Relaciones Internacionales", "Ciencia Política", "Ciencias Políticas y Administración Pública",
        "Administración Pública", "Políticas Públicas", "Economía", "Economía Internacional",
        "Desarrollo Económico", "Finanzas Públicas", "Sociología", "Antropología Social",
        "Antropología Física", "Etnología", "Psicología", "Psicología Clínica", "Psicología Organizacional",
        "Psicología Educativa", "Psicología Social", "Trabajo Social", "Desarrollo Social",
        "Intervención Social", "Comunicación", "Ciencias de la Comunicación", "Comunicación Organizacional",
        "Comunicación Política", "Periodismo", "Periodismo Digital", "Periodismo de Investigación",
        "Comunicación Audiovisual", "Medios Digitales", "Producción de Medios", "Historia",
        "Historia del Arte", "Filosofía", "Filosofía y Letras", "Letras Hispánicas", "Literatura",
        "Lengua y Literaturas Modernas", "Estudios Latinoamericanos", "Estudios Internacionales",
        "Estudios Globales", "Geografía", "Geografía Humana", "Desarrollo Territorial", "Urbanismo",
        "Estudios Urbanos", "Planificación Territorial", "Educación", "Pedagogía", "Ciencias de la Educación",
        "Docencia", "Educación Especial", "Educación Inicial", "Innovación Educativa",
        "Tecnología Educativa", "Gestión Cultural", "Administración Cultural", "Promoción Cultural",
        "Bibliotecología", "Archivonomía", "Ciencias de la Información", "Estudios de Género",
        "Desarrollo Humano", "Estudios de la Paz", "Resolución de Conflictos", "Cooperación Internacional",
        "Teología", "Filosofía y Teología", "Lenguas Modernas", "Traducción e Interpretación",
        "Lingüística", "Lingüística Aplicada", "Enseñanza de Idiomas"
      ],
      schools: [
        "UNAM - Universidad Nacional Autónoma de México: Facultades de Derecho, Ciencias Políticas y Sociales, Filosofía y Letras, Economía, Psicología (Ciudad Universitaria, CDMX)",
        "IPN - Instituto Politécnico Nacional: ESCA (Escuela Superior de Comercio y Administración)",
        "UAM - Universidad Autónoma Metropolitana: Unidades Xochimilco, Iztapalapa, Azcapotzalco (Ciencias Sociales y Humanidades)",
        "Tec de Monterrey: Carreras en Ciencias Sociales, Derecho, Relaciones Internacionales, Comunicación",
        "Universidad Iberoamericana (IBERO): Derecho, Psicología, Relaciones Internacionales, Comunicación, Filosofía",
        "Universidad La Salle: Derecho, Psicología, Ciencias de la Comunicación, Filosofía",
        "Universidad Anáhuac: Derecho, Relaciones Internacionales, Comunicación, Psicología",
        "Universidad Panamericana (UP): Derecho, Comunicación, Psicología, Pedagogía",
        "El Colegio de México (COLMEX): Posgrados de excelencia en Ciencias Sociales",
        "FLACSO México: Ciencias Sociales con enfoque latinoamericano",
        "CIDE - Centro de Investigación y Docencia Económicas: Economía, Ciencia Política, Relaciones Internacionales",
        "Centro Universitario Grupo Sol (CUGS): Administración, Relaciones Internacionales, Derecho (según oferta)",
        "UNITEC: Derecho, Psicología, Comunicación, Criminología",
        "Universidad Insurgentes: Derecho, Psicología, Comunicación",
        "Universidad del Valle de México (UVM): Derecho, Psicología, Comunicación",
        "Universidad Autónoma de Guadalajara (UAG): Derecho, Psicología, Comunicación",
        "Universidad de Guadalajara (UDG): CUCSH - Centro Universitario de Ciencias Sociales y Humanidades",
        "Universidad Autónoma de Nuevo León (UANL): Facultades de Derecho, Psicología, Ciencias Políticas, Filosofía y Letras",
        "Benemérita Universidad Autónoma de Puebla (BUAP): Facultades de Derecho, Filosofía y Letras, Psicología",
        "Universidad Veracruzana (UV): Múltiples facultades de Ciencias Sociales",
        "Universidad Autónoma del Estado de México (UAEM): Facultades de Derecho, Ciencias Políticas, Humanidades",
        "Universidad Rosario Castellanos: Derecho, Comunicación, Pedagogía",
        "Universidad Intercontinental (UIC): Psicología, Comunicación, Derecho",
        "Universidad Simón Bolívar: Derecho, Comunicación",
        "Universidad Latinoamericana (ULA): Derecho, Psicología, Comunicación",
        "UANL, UDG, BUAP, UAEM, UV: Amplia oferta en humanidades según campus"
      ]
    },
    "Arte y Diseño": {
      careers: [
        "Arquitectura", "Arquitectura de Interiores", "Arquitectura del Paisaje", "Urbanismo",
        "Diseño Urbano", "Diseño Gráfico", "Diseño Digital", "Diseño de Comunicación Visual",
        "Diseño Editorial", "Diseño de Información", "Diseño de Interiores", "Interiorismo",
        "Diseño de Espacios", "Diseño Industrial", "Diseño de Producto", "Diseño de Muebles",
        "Diseño de Transporte", "Diseño de Moda", "Diseño Textil", "Diseño de Vestuario",
        "Estilismo", "Diseño Multimedia", "Diseño Interactivo", "Diseño de Experiencia de Usuario (UX/UI)",
        "Diseño Web", "Diseño de Videojuegos", "Game Design", "Animación Digital",
        "Animación 2D y 3D", "Animación y Efectos Visuales", "Motion Graphics", "Arte Digital",
        "Ilustración Digital", "Concept Art", "Artes Visuales", "Artes Plásticas", "Pintura",
        "Escultura", "Grabado", "Bellas Artes", "Historia del Arte", "Gestión Cultural y Artística",
        "Curaduría", "Fotografía", "Fotografía Digital", "Fotoperiodismo", "Fotografía Publicitaria",
        "Cine", "Cinematografía", "Dirección Cinematográfica", "Producción Cinematográfica",
        "Guionismo", "Comunicación Audiovisual", "Producción Audiovisual", "Producción de Televisión",
        "Producción de Contenidos Digitales", "Dirección de Arte", "Dirección Creativa",
        "Publicidad", "Publicidad y Medios", "Creatividad Publicitaria", "Música",
        "Composición Musical", "Producción Musical", "Ingeniería en Audio", "Sonido",
        "Educación Musical", "Interpretación Musical", "Canto", "Teatro", "Actuación",
        "Dirección Escénica", "Dramaturgia", "Producción Teatral", "Escenografía",
        "Diseño Escénico", "Iluminación Escénica", "Danza", "Coreografía", "Pedagogía de la Danza",
        "Literatura Creativa", "Escritura Creativa", "Creación Literaria", "Artesanías y Diseño",
        "Restauración de Arte", "Conservación del Patrimonio"
      ],
      schools: [
        "UNAM: Facultad de Artes y Diseño (FAD) en Xochimilco y Ciudad Universitaria, Escuela Nacional de Artes Plásticas, CUEC (Centro Universitario de Estudios Cinematográficos), Escuela Nacional de Música",
        "UAM: Unidad Xochimilco y Azcapotzalco - Diseño, Comunicación Visual",
        "Universidad Iberoamericana (IBERO): Diseño, Arte, Arquitectura, Comunicación",
        "Universidad La Salle: Arquitectura, Diseño Gráfico, Diseño de Moda, Producción Audiovisual",
        "Universidad Anáhuac: Arquitectura, Diseño, Comunicación",
        "IPN: ESIA (Escuela Superior de Ingeniería y Arquitectura) - Arquitectura y Diseño Industrial",
        "Tec de Monterrey: Arquitectura, Diseño, Arte Digital, Animación (varios campus)",
        "Centro de Diseño, Cine y Televisión (CENTRO): Diseño Gráfico, Cine, Televisión",
        "CENTRO de Diseño Cine y Televisión: Programas especializados en cine y diseño",
        "Escuela de Diseño del INBA: Diseño Gráfico, Textil, Industrial",
        "CEDIM - Centro de Estudios Superiores de Diseño de Monterrey",
        "Universidad de Monterrey (UDEM): Arquitectura y Diseño",
        "Universidad de Guadalajara (UDG): CUAAD - Centro Universitario de Arte, Arquitectura y Diseño",
        "ITESO: Arquitectura, Diseño",
        "UNITEC: Arquitectura, Diseño Gráfico, Diseño Digital",
        "Universidad del Valle de México (UVM): Arquitectura, Diseño, Comunicación Audiovisual",
        "Universidad Iberoamericana León: Arquitectura y Diseño",
        "Universidad Autónoma de Nuevo León (UANL): Facultad de Arquitectura, Artes Visuales, Música",
        "Universidad Autónoma de Aguascalientes (UAA): Diseño Gráfico, Arquitectura",
        "Benemérita Universidad Autónoma de Puebla (BUAP): Facultad de Arquitectura, Bellas Artes",
        "Universidad Veracruzana (UV): Facultades de Artes, Arquitectura",
        "Universidad de las Américas Puebla (UDLAP): Arquitectura, Diseño, Artes",
        "Centro de Estudios Cinematográficos (CCC): Especializado en cine",
        "Escuela Nacional de Pintura, Escultura y Grabado 'La Esmeralda': INBA",
        "Escuela Superior de Música: INBA",
        "Escuela Nacional de Danza Clásica y Contemporánea: INBA",
        "Centro Nacional de las Artes (CENART): Múltiples disciplinas artísticas",
        "Centro Universitario Grupo Sol (CUGS): Opciones en áreas creativas (según oferta)",
        "Universidad Rosario Castellanos: Comunicación y Artes",
        "SAE Institute: Audio, Animación, Videojuegos",
        "Escuela de Artes Visuales (AVE): Arte digital y diseño"
      ]
    },
    "Ciencias de la Salud": {
      careers: [
        "Medicina General", "Médico Cirujano", "Cirugía", "Medicina Especializada (múltiples especialidades vía residencia)",
        "Pediatría", "Ginecología y Obstetricia", "Medicina Interna", "Cirugía General", "Anestesiología", "Radiología",
        "Medicina de Urgencias", "Medicina Familiar", "Geriatría", "Odontología", "Cirugía Maxilofacial", "Ortodoncia",
        "Periodoncia", "Endodoncia", "Odontopediatría", "Enfermería", "Enfermería y Obstetricia", "Enfermería Quirúrgica",
        "Enfermería Pediátrica", "Enfermería en Cuidados Intensivos", "Fisioterapia", "Terapia Física y Rehabilitación",
        "Rehabilitación", "Kinesiología", "Nutrición", "Nutrición Clínica", "Nutrición Deportiva", "Nutrición Pediátrica",
        "Ciencias de la Nutrición Humana", "Psicología Clínica", "Psicología de la Salud", "Psicoterapia", "Neuropsicología",
        "Psiquiatría (vía medicina)", "Optometría", "Ciencias de la Visión", "Audiología", "Terapia del Lenguaje",
        "Terapia de Lenguaje y Audición", "Patología del Habla", "Terapia Ocupacional", "Terapia Respiratoria", "Inhaloterapia",
        "Medicina Veterinaria", "Veterinaria y Zootecnia", "Medicina Veterinaria Clínica", "Medicina Veterinaria de Animales de Producción",
        "Medicina Veterinaria de Fauna Silvestre", "Química Farmacéutica Biológica (QFB)", "Farmacia", "Farmacología",
        "Química Farmacéutica Industrial", "Farmacia Clínica", "Biotecnología Médica", "Ingeniería Biotecnológica en Salud",
        "Bioingeniería Médica", "Ingeniería Biomédica", "Biomedicina", "Ciencias Biomédicas", "Investigación Biomédica Básica",
        "Biología Médica", "Bioquímica Clínica", "Bioquímica Diagnóstica", "Laboratorio Clínico", "Química Clínica", "Análisis Clínicos",
        "Ciencias Genómicas", "Genética Médica", "Medicina Genómica", "Radiología e Imagen", "Imagenología Diagnóstica y Terapéutica",
        "Ultrasonografía", "Gerontología", "Geriatría y Gerontología", "Cuidados Paliativos", "Medicina Estética", "Cosmetología Médica",
        "Dermatología (vía medicina)", "Salud Pública", "Epidemiología", "Administración de Servicios de Salud", "Gestión de Instituciones de Salud",
        "Informática de la Salud", "Salud Digital", "Podología", "Ciencias del Deporte y Actividad Física (enfoque salud)",
        "Medicina del Deporte", "Citohistopatología", "Citotecnología", "Embriología", "Hematología", "Inmunología", "Bacteriología",
        "Microbiología Médica", "Virología", "Parasitología"
      ],
      schools: [
        "UNAM: Facultad de Medicina, Facultad de Odontología, Facultad de Medicina Veterinaria y Zootecnia, Facultad de Química, Escuela Nacional de Enfermería y Obstetricia, FES Iztacala, FES Zaragoza (CDMX y Estado de México)",
        "IPN: Escuela Nacional de Ciencias Biológicas (ENCB), Centro Interdisciplinario de Ciencias de la Salud (CICS), Escuela Superior de Medicina (ESM)",
        "UAM: Carreras en Ciencias de la Salud (Unidades Xochimilco e Iztapalapa)",
        "Tec de Monterrey: Medicina, Nutrición, Psicología, Biotecnología (varios campus)",
        "Universidad Anáhuac: Medicina, Odontología, Nutrición, Psicología",
        "Universidad Panamericana (UP): Medicina, Odontología, Nutrición",
        "Universidad La Salle: Medicina, Odontología, Enfermería, Nutrición, Psicología Clínica",
        "Universidad Iberoamericana (IBERO): Psicología, Nutrición",
        "UNITEC: Enfermería, Nutrición, Psicología de la Salud",
        "Universidad del Valle de México (UVM): Medicina, Odontología, Enfermería, Nutrición, Psicología",
        "Universidad Westhill: Medicina, Odontología",
        "Universidad Autónoma de Guadalajara (UAG): Medicina, Odontología (una de las más antiguas de México en medicina privada)",
        "Universidad de Guadalajara (UDG): Centro Universitario de Ciencias de la Salud",
        "Universidad Autónoma de Nuevo León (UANL): Facultad de Medicina, Odontología, Enfermería, Salud Pública y Nutrición, Psicología",
        "Benemérita Universidad Autónoma de Puebla (BUAP): Facultad de Medicina, Enfermería, Químico Farmacobiología",
        "Universidad Veracruzana (UV): Facultades de Medicina, Enfermería, Nutrición, Odontología",
        "Universidad Autónoma de Baja California (UABC): Facultad de Medicina y Psicología, Odontología, Enfermería",
        "Universidad de Sonora (UNISON): División de Ciencias Biológicas y de la Salud",
        "Universidad Autónoma de Coahuila (UAdeC): Facultad de Medicina",
        "Universidad Autónoma de Yucatán (UADY): Facultad de Medicina, Odontología, Enfermería",
        "Universidad Autónoma del Estado de México (UAEM): Facultades de Medicina, Enfermería y Obstetricia",
        "Universidad Autónoma de San Luis Potosí (UASLP): Facultad de Medicina",
        "Universidad de Guanajuato (UG): Campus León - División de Ciencias de la Salud",
        "Universidad Autónoma de Querétaro (UAQ): Facultad de Medicina, Enfermería",
        "Universidad Autónoma de Sinaloa (UAS): Facultad de Medicina",
        "Universidad Juárez del Estado de Durango (UJED): Facultad de Medicina",
        "Universidad Autónoma de Chihuahua (UACH): Facultad de Medicina",
        "Centro Universitario Grupo Sol (CUGS): Opciones en Psicología y áreas de salud (según disponibilidad)",
        "Universidad Rosario Castellanos: Enfermería, Psicología",
        "Universidad Insurgentes: Psicología Clínica, Nutrición",
        "UNITEC, UVM, UNID: Múltiples opciones en salud en varios campus"
      ]
    },
    "Empresarial": {
      careers: [
        "Administración de Empresas", "Licenciatura en Administración", "Administración y Dirección de Empresas",
        "Gestión Empresarial", "Negocios Internacionales", "Comercio Internacional", "Negocios Globales",
        "Gestión de Negocios Internacionales", "Finanzas", "Finanzas Corporativas", "Banca y Finanzas",
        "Ingeniería Financiera", "Administración Financiera", "Fintech (Tecnología Financiera)", "Contaduría Pública",
        "Contaduría", "Contaduría y Finanzas", "Auditoría", "Contaduría Gubernamental", "Economía Empresarial",
        "Economía y Negocios", "Análisis Económico Empresarial", "Mercadotecnia", "Marketing", "Marketing Digital",
        "Marketing y Ventas", "Dirección de Marketing", "Mercadotecnia Estratégica", "Publicidad", "Publicidad y Marketing",
        "Comunicación Publicitaria", "Estrategia Publicitaria", "Emprendimiento", "Emprendimiento e Innovación",
        "Creación y Desarrollo de Empresas", "Emprendimiento de Negocios", "Gestión de Recursos Humanos",
        "Administración del Capital Humano", "Recursos Humanos y Relaciones Laborales", "Desarrollo Organizacional",
        "Psicología Organizacional y del Trabajo", "Dirección de Proyectos", "Gestión de Proyectos", "Project Management",
        "Administración de Proyectos", "Logística", "Logística y Cadena de Suministro", "Supply Chain Management",
        "Logística Internacional", "Administración Logística", "Comercio Electrónico (E-commerce)", "Negocios Digitales",
        "Comercio Digital", "Marketing Digital y E-commerce", "Administración Hotelera", "Administración de Empresas Turísticas",
        "Gestión Hotelera", "Hotelería y Turismo", "Turismo", "Turismo Sustentable", "Gestión Turística",
        "Administración de Restaurantes", "Gastronomía y Administración", "Gestión de Alimentos y Bebidas",
        "Gestión de la Innovación", "Innovación y Estrategia de Negocios", "Transformación Digital de Negocios",
        "Relaciones Laborales", "Relaciones Industriales", "Derecho Laboral y Recursos Humanos", "Gestión Comercial",
        "Dirección Comercial", "Ventas Empresariales", "Inteligencia de Negocios (Business Intelligence)",
        "Analítica de Negocios (Business Analytics)", "Ciencia de Datos para Negocios",
        "Administración Pública y Políticas Públicas (con enfoque empresarial)", "Gestión Pública",
        "Responsabilidad Social Empresarial", "Sustentabilidad Empresarial", "Consultoría de Negocios",
        "Estrategia Empresarial", "Dirección Estratégica", "Comercio y Negocios", "Administración Industrial",
        "Ingeniería en Gestión Empresarial", "Ingeniería Administrativa", "Mercados Financieros",
        "Actuaría (con enfoque empresarial)", "Administración Deportiva", "Gestión del Deporte",
        "Administración de Bienes Raíces", "Real Estate", "Franquicias y Desarrollo de Negocios"
      ],
      schools: [
        "Tec de Monterrey (ITESM): Líder en Negocios, Administración, Finanzas, Emprendimiento (todos los campus)",
        "IPADE Business School: Escuela de negocios de élite (posgrados y educación ejecutiva)",
        "EGADE Business School (Tec de Monterrey): Una de las mejores escuelas de negocios de Latinoamérica",
        "UNAM: Facultad de Contaduría y Administración (FCA), División de Estudios de Posgrado",
        "IPN: ESCA (Escuela Superior de Comercio y Administración) - Santo Tomás y Tepepan",
        "UAM: Carreras en Administración, Economía (Azcapotzalco, Iztapalapa)",
        "Universidad Iberoamericana (IBERO): Negocios, Administración, Finanzas, Marketing",
        "Universidad Panamericana (UP): IPADE, programas de negocios de alto nivel",
        "Universidad Anáhuac: Facultad de Economía y Negocios, Escuela de Negocios",
        "Universidad La Salle: Administración, Contaduría, Negocios",
        "ITAM - Instituto Tecnológico Autónomo de México: Economía, Administración, Contaduría, Actuaría",
        "CIDE: Economía, Administración Pública",
        "Centro Universitario Grupo Sol (CUGS): Administración, Contaduría, Negocios Internacionales",
        "UNITEC: Administración, Mercadotecnia, Contaduría, Comercio Internacional",
        "Universidad del Valle de México (UVM): Administración, Negocios, Finanzas, Marketing",
        "Universidad Insurgentes: Administración, Comercio, Contaduría",
        "Universidad Latinoamericana (ULA): Negocios y Administración",
        "Universidad Autónoma de Guadalajara (UAG): Negocios, Administración",
        "Universidad de Guadalajara (UDG): CUCEA - Centro Universitario de Ciencias Económico Administrativas",
        "Universidad Autónoma de Nuevo León (UANL): FACPyA - Facultad de Contaduría Pública y Administración, FACPYA",
        "Benemérita Universidad Autónoma de Puebla (BUAP): Facultad de Administración, Contaduría",
        "Universidad Veracruzana (UV): Facultad de Contaduría y Administración",
        "Universidad Autónoma del Estado de México (UAEM): Facultad de Contaduría y Administración",
        "Universidad de Monterrey (UDEM): Negocios, Administración",
        "ITESO: Administración, Mercadotecnia, Finanzas",
        "Universidad de las Américas Puebla (UDLAP): Negocios, Administración, Finanzas",
        "Universidad Rosario Castellanos: Administración, Contaduría",
        "Tecnológico Nacional de México (TecNM): Ingeniería en Gestión Empresarial (254 campus)",
        "Universidad Autónoma de Querétaro (UAQ): Contaduría y Administración",
        "Universidad Autónoma de Aguascalientes (UAA): Centro de Ciencias Económicas y Administrativas",
        "Universidad de Sonora (UNISON): División de Ciencias Económicas y Administrativas",
        "UANL, UDG, BUAP, UV, UAEM: Amplias ofertas en todas las carreras empresariales"
      ]
    }
  };

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/users/me`,
          {
            headers: { Authorization: token },
          }
        );
        setResults(response.data.VocationalUniversityResults || {});
        setUser(response.data);
      } catch (error) {
        console.error("Error al obtener resultados:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, []);

  return (
    <>
      <Navbar />

      <div className="dashboard-layout">
        {/* Sidebar */}
        <div className="dashboard-sidebar">
          <div className="sidebar-header">
            <h3>Orientación Vocacional Nivel Superior</h3>
          </div>
          <div className="sidebar-menu">
            <button
              className={`menu-item ${activeSection === "overview" ? "active" : ""
                }`}
              onClick={() => setActiveSection("overview")}
            >
              <i className="bi bi-speedometer2"></i> Resumen
            </button>
            <button
              className="menu-item return-dashboard"
              onClick={() => navigate("/dashboard")}
            >
              <i className="bi bi-arrow-left-circle"></i> Volver al Dashboard
            </button>
          </div>
        </div>

        {/* Main */}
        <div className="dashboard-main">
          <div className="results-content">
            {loading ? (
              <p className="text-center mt-5">Cargando resultados...</p>
            ) : !results || !results.totalScores ? (
              <p className="text-center mt-5">
                No se encontraron resultados vocacionales.
              </p>
            ) : (
              <>
                <h3 className="text-center mb-4">
                  Resultados de Orientación Vocacional Nivel Superior
                </h3>
                <p className="text-muted text-center">
                  Esta evaluación analiza tu afinidad con cinco áreas
                  profesionales.
                </p>

                {/* Área con mayor afinidad */}
                <div className="text-center mb-4">
                  <h4>Área con mayor afinidad:</h4>
                  <div className="alert alert-success fs-5 fw-bold mt-2">
                    {results.bestArea} ({results.bestScore} puntos)
                  </div>
                </div>

                {/* Resumen por área */}
                <div className="skills-summary">
                  <h5 className="mb-3">Resumen por Área</h5>
                  <div className="skills-chart">
                    {Object.entries(results.totalScores).map(([area, data]) => (
                      <div key={area} className="chart-bar mb-3">
                        <div className="bar-label">
                          <strong>{area}</strong> – {data.level}
                        </div>
                        <div className="bar-container">
                          <div
                            className="bar-fill"
                            style={{
                              width: `${(data.score / 36) * 100}%`,
                              backgroundColor:
                                data.level === "Alta Afinidad"
                                  ? "#28a745"
                                  : data.level === "Afinidad Moderada"
                                    ? "#ffc107"
                                    : "#dc3545",
                            }}
                          >
                            {data.score} pts
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Carreras y universidades */}
                {results.bestArea &&
                  vocationalRecommendations[results.bestArea] && (
                    <div className="career-recommendations mt-5">
                      <h4 className="text-center mb-4 text-primary fw-bold">
                        <i className="bi bi-star-fill text-warning me-2"></i>
                        Recomendaciones para tu perfil
                      </h4>
                      <div className="recommendation-box bg-white p-4 rounded shadow-sm border mb-4">
                        <h5 className="mb-3 text-secondary fw-bold border-bottom pb-2">
                          <i className="bi bi-briefcase-fill me-2 text-info"></i>
                          Carreras Universitarias Sugeridas
                        </h5>
                        <div className="d-flex flex-wrap gap-2 mb-5">
                          {vocationalRecommendations[results.bestArea].careers.map(
                            (career, i) => (
                              <span key={i} className="badge rounded-pill bg-primary fw-normal p-2 fs-6 shadow-sm">
                                {career}
                              </span>
                            )
                          )}
                        </div>

                        <h5 className="mb-3 text-secondary fw-bold border-bottom pb-2">
                          <i className="bi bi-building-fill me-2 text-danger"></i>
                          Universidades con Mayor Afinidad y Prestigio
                        </h5>
                        <div className="row g-3">
                          {vocationalRecommendations[results.bestArea].schools.map(
                            (school, i) => (
                              <div key={i} className="col-md-6 col-lg-6">
                                <div className="card shadow-sm h-100 border-0 bg-light">
                                  <div className="card-body d-flex align-items-center">
                                    <i className="bi bi-mortarboard-fill text-secondary fs-3 me-3"></i>
                                    <p className="card-text mb-0 small text-dark fw-medium lh-sm">
                                      {school}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                {/* PDF */}
                <div className="results-actions text-center mt-4">
                  <PDFDownloadLink
                    document={
                      <ResultsPDF
                        user={user}
                        results={results}
                        recommendations={vocationalRecommendations}
                      />
                    }
                    fileName={`Resultados_Vocacionales_Superior_${new Date()
                      .toLocaleDateString()
                      .replaceAll("/", "_")}.pdf`}
                  >
                    {({ loading }) =>
                      loading ? (
                        <button className="btn btn-secondary">
                          Generando PDF...
                        </button>
                      ) : (
                        <button className="btn btn-primary">
                          <i className="bi bi-download"></i> Descargar Resultados
                        </button>
                      )
                    }
                  </PDFDownloadLink>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default VocationalUniversityResults;
